/* eslint-disable react-refresh/only-export-components -- data router module exports route tree, not React components */
import { Suspense, type ReactNode } from 'react'
import { setPendingHrefGetter } from '@/utils/chunk-recovery'
import {
  Navigate,
  Route,
  createBrowserRouter,
  createRoutesFromElements,
  useLocation,
  type RouteObject,
} from 'react-router-dom'
import { installListMemory, listMemoryLoader, listMemoryShouldRevalidate } from '@/utils/list-memory'
import { lazyRoute as lazy } from '@/utils/lazy-route'
import RouteErrorPage from '@/modules/errors/pages/RouteErrorPage'
import { AppLayout } from '@/layouts/AppLayout'
import { AuthLayout } from '@/layouts/AuthLayout'
import { ProtectedRoute } from '@/app/router/ProtectedRoute'
import { PublicRoute } from '@/app/router/PublicRoute'
import { RouteLoadingBar } from '@/components/common/RouteLoadingBar'
import { ROUTES } from '@/constants/routes'
import type { PermissionKey } from '@/types/permission.types'
import { useAuth } from '@/hooks/useAuth'
import { getPermissionDeniedPath } from '@/utils/home-path'

const LoginPage = lazy(() => import('@/modules/auth/pages/LoginPage'))
const ChangePasswordPage = lazy(() => import('@/modules/auth/pages/ChangePasswordPage'))
const MyDevicesPage = lazy(() => import('@/modules/auth/pages/MyDevicesPage'))
const PinSetupPage = lazy(() => import('@/modules/auth/pages/PinSetupPage'))
const ChangePinPage = lazy(() => import('@/modules/auth/pages/ChangePinPage'))
const SecuritySettingsPage = lazy(() => import('@/modules/settings/pages/SecuritySettingsPage'))
const BackupsPage = lazy(() => import('@/modules/settings/pages/BackupsPage'))
const EmailSettingsPage = lazy(() => import('@/modules/settings/pages/EmailSettingsPage'))
const SiteFormSettingsPage = lazy(() => import('@/modules/settings/pages/SiteFormSettingsPage'))
const DashboardPage = lazy(
  () => import('@/modules/dashboard/pages/DashboardPage'),
)
const MasterDataOverviewPage = lazy(
  () => import('@/modules/master-data/pages/MasterDataOverviewPage'),
)
const CustomersListPage = lazy(
  () => import('@/modules/master-data/pages/customers/CustomersListPage'),
)
const CustomerCreatePage = lazy(
  () => import('@/modules/master-data/pages/customers/CustomerCreatePage'),
)
const CustomerEditPage = lazy(
  () => import('@/modules/master-data/pages/customers/CustomerEditPage'),
)
const CustomerViewPage = lazy(
  () => import('@/modules/master-data/pages/customers/CustomerViewPage'),
)
const CategoriesPage = lazy(
  () => import('@/modules/master-data/pages/CategoriesPage'),
)
const DesignsListPage = lazy(
  () => import('@/modules/master-data/pages/products/DesignsListPage'),
)
const DesignCreatePage = lazy(
  () => import('@/modules/master-data/pages/products/DesignCreatePage'),
)
const ProductDetailPage = lazy(
  () => import('@/modules/master-data/pages/products/ProductDetailPage'),
)
const CatalogListPage = lazy(
  () => import('@/modules/master-data/pages/catalog/CatalogListPage'),
)
const CatalogFormPage = lazy(
  () => import('@/modules/master-data/pages/catalog/CatalogFormPage'),
)
const CatalogDetailPage = lazy(
  () => import('@/modules/master-data/pages/catalog/CatalogDetailPage'),
)
const CatalogSkusPage = lazy(
  () => import('@/modules/master-data/pages/catalog/CatalogSkusPage'),
)
const CategoryViewPage = lazy(
  () => import('@/modules/master-data/pages/CategoryViewPage'),
)
const QualitiesPage = lazy(
  () => import('@/modules/master-data/pages/QualitiesPage'),
)
const ColoursPage = lazy(
  () => import('@/modules/master-data/pages/ColoursPage'),
)
const SuppliersListPage = lazy(
  () => import('@/modules/master-data/pages/suppliers/SuppliersListPage'),
)
const SupplierCreatePage = lazy(
  () => import('@/modules/master-data/pages/suppliers/SupplierCreatePage'),
)
const SupplierEditPage = lazy(
  () => import('@/modules/master-data/pages/suppliers/SupplierEditPage'),
)
const SupplierViewPage = lazy(
  () => import('@/modules/master-data/pages/suppliers/SupplierViewPage'),
)
const SalesAgentsPage = lazy(
  () => import('@/modules/master-data/pages/SalesAgentsPage'),
)
const SalesLeadsListPage = lazy(
  () => import('@/modules/sales/pages/leads/SalesLeadsHubPage'),
)
const SalesLeadCreatePage = lazy(
  () => import('@/modules/sales/pages/leads/SalesLeadCreatePage'),
)
const SalesLeadViewPage = lazy(
  () => import('@/modules/sales/pages/leads/SalesLeadViewPage'),
)
const OldCustomerViewPage = lazy(
  () => import('@/modules/sales/pages/leads/OldCustomerViewPage'),
)
const SiteInboxReviewPage = lazy(
  () => import('@/modules/sales/pages/leads/SiteInboxReviewPage'),
)
const SalesLeadRegisterPage = lazy(
  () => import('@/modules/sales/pages/leads/SalesLeadRegisterPage'),
)
const QuotationsListPage = lazy(
  () => import('@/modules/sales/pages/quotations/QuotationsListPage'),
)
const QuotationCreatePage = lazy(
  () => import('@/modules/sales/pages/quotations/QuotationCreatePage'),
)
const QuotationDetailPage = lazy(
  () => import('@/modules/sales/pages/quotations/QuotationDetailPage'),
)
const EstimatesListPage = lazy(
  () => import('@/modules/sales/pages/estimates/EstimatesListPage'),
)
const EstimateCreatePage = lazy(
  () => import('@/modules/sales/pages/estimates/EstimateCreatePage'),
)
const EstimateDetailPage = lazy(
  () => import('@/modules/sales/pages/estimates/EstimateDetailPage'),
)
const BankAccountsPage = lazy(
  () => import('@/modules/company/pages/BankAccountsPage'),
)
const WarehousesPage = lazy(
  () => import('@/modules/master-data/pages/WarehousesPage'),
)
const ReportsLayout = lazy(() => import('@/modules/reports/pages/ReportsLayout'))
const ReportsHomeRedirect = lazy(
  () => import('@/modules/reports/pages/ReportsHomeRedirect'),
)
const SummaryOverviewPage = lazy(
  () => import('@/modules/reports/pages/SummaryOverviewPage'),
)
const SummaryTrendPage = lazy(
  () => import('@/modules/reports/pages/SummaryTrendPage'),
)
const DetailExportsPage = lazy(
  () => import('@/modules/reports/pages/DetailExportsPage'),
)
const DispatchSheetListPage = lazy(
  () => import('@/modules/dispatch/pages/DispatchSheetListPage'),
)
const DispatchSheetCreatePage = lazy(
  () => import('@/modules/dispatch/pages/DispatchSheetCreatePage'),
)
const DispatchSheetViewPage = lazy(
  () => import('@/modules/dispatch/pages/DispatchSheetViewPage'),
)
const DispatchSheetEditPage = lazy(
  () => import('@/modules/dispatch/pages/DispatchSheetEditPage'),
)
const PurchaseSheetListPage = lazy(
  () => import('@/modules/purchases/pages/PurchaseSheetListPage'),
)
const PurchaseSheetCreatePage = lazy(
  () => import('@/modules/purchases/pages/PurchaseSheetCreatePage'),
)
const PurchaseSheetViewPage = lazy(
  () => import('@/modules/purchases/pages/PurchaseSheetViewPage'),
)
const PurchaseSheetEditPage = lazy(
  () => import('@/modules/purchases/pages/PurchaseSheetEditPage'),
)
const TasksPage = lazy(() => import('@/modules/tasks/pages/TasksPage'))
const TaskCreatePage = lazy(() => import('@/modules/tasks/pages/TaskCreatePage'))
const TaskEditPage = lazy(() => import('@/modules/tasks/pages/TaskEditPage'))
const MessengerPage = lazy(() => import('@/modules/messenger/pages/MessengerPage'))
const TaskDetailPage = lazy(() => import('@/modules/tasks/pages/TaskDetailPage'))
const UsersListPage = lazy(() => import('@/modules/users/pages/UsersListPage'))
const UserCreatePage = lazy(() => import('@/modules/users/pages/UserCreatePage'))
const UserEditPage = lazy(() => import('@/modules/users/pages/UserEditPage'))
const UserViewPage = lazy(() => import('@/modules/users/pages/UserViewPage'))
const AgentLinksPage = lazy(() => import('@/modules/users/pages/AgentLinksPage'))
const PermissionsPage = lazy(
  () => import('@/modules/users/pages/PermissionsPage'),
)
const WorkspacePage = lazy(
  () => import('@/modules/workspace/pages/WorkspacePage'),
)
const NotificationsPage = lazy(
  () => import('@/modules/notifications/pages/NotificationsPage'),
)
const MobileSettingsPage = lazy(
  () => import('@/modules/settings/pages/MobileSettingsPage'),
)
const MobileSharePage = lazy(
  () => import('@/modules/share-intake/pages/MobileSharePage'),
)
const SettingsPage = lazy(
  () => import('@/modules/settings/pages/SettingsPage'),
)
const SheetsSyncPage = lazy(
  () => import('@/modules/settings/pages/SheetsSyncPage'),
)
const QuotationTermsTemplatesPage = lazy(
  () => import('@/modules/settings/pages/QuotationTermsTemplatesPage'),
)
const UnauthorizedPage = lazy(
  () => import('@/modules/errors/pages/UnauthorizedPage'),
)
const ForbiddenPage = lazy(
  () => import('@/modules/errors/pages/ForbiddenPage'),
)
const NotFoundPage = lazy(() => import('@/modules/errors/pages/NotFoundPage'))

/** Standalone routes (login, errors) own their Suspense boundary. */
function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<RouteLoadingBar />}>{children}</Suspense>
}

/**
 * Pages inside AppLayout suspend to the shell's single persistent boundary
 * (see AppLayout `RouteOutlet`) — a fresh boundary per route would blank the
 * current page on every navigation instead of keeping it until the next is ready.
 */
function InShell({ children }: { children: ReactNode }) {
  return <>{children}</>
}

function Guard({
  permission,
  children,
}: {
  permission: PermissionKey | PermissionKey[]
  children: ReactNode
}) {
  const { hasPermission, user, isHydrated } = useAuth()
  const { pathname } = useLocation()

  // Mirrors ProtectedRoute's own wait — Guard only ever renders under it, so
  // isHydrated is normally already true by the time this mounts, but this
  // keeps that invariant explicit rather than assumed, so a future reorder
  // of the route tree can't reintroduce a denied-then-allowed flash.
  if (!isHydrated || !user) return null

  if (user.role === 'super_admin') {
    return <>{children}</>
  }
  const keys = Array.isArray(permission) ? permission : [permission]
  if (keys.some((key) => hasPermission(key))) {
    return <>{children}</>
  }
  return <Navigate to={getPermissionDeniedPath(user, pathname)} replace />
}

function page(permission: PermissionKey | PermissionKey[], element: ReactNode) {
  return (
    <InShell>
      <Guard permission={permission}>{element}</Guard>
    </InShell>
  )
}

/** List pages whose page / size / tab / filters / search are remembered (see list-memory). */
const REMEMBERED_LIST_PATHS = [
  ROUTES.SALES_LEADS,
  ROUTES.QUOTATIONS,
  ROUTES.ESTIMATES,
  ROUTES.QUOTATION_TERMS_TEMPLATES,
  ROUTES.TASKS,
  ROUTES.CATALOG,
  ROUTES.PRODUCTS,
  ROUTES.CATEGORIES,
  ROUTES.QUALITIES,
  ROUTES.COLOURS,
  ROUTES.WAREHOUSES,
  ROUTES.SALES_AGENTS,
  ROUTES.CUSTOMERS,
  ROUTES.SUPPLIERS,
  ROUTES.DISPATCH_SHEET,
  ROUTES.PURCHASE_SHEET,
  ROUTES.USERS,
] as const

function withListMemory(routes: RouteObject[]): RouteObject[] {
  const paths = new Set<string>(REMEMBERED_LIST_PATHS)
  for (const route of routes) {
    if (route.path && paths.has(route.path)) {
      route.loader = listMemoryLoader
      route.shouldRevalidate = listMemoryShouldRevalidate
      // The loader is synchronous; nothing to show while it "loads".
      route.HydrateFallback = () => null
    }
    if (route.children) withListMemory(route.children)
  }
  return routes
}

export const appRouter = createBrowserRouter(
  withListMemory(createRoutesFromElements(
    <Route errorElement={<RouteErrorPage />}>
      <Route element={<PublicRoute />}>
        <Route element={<AuthLayout />}>
          <Route
            path={ROUTES.LOGIN}
            element={
              <Lazy>
                <LoginPage />
              </Lazy>
            }
          />
        </Route>
      </Route>

      <Route
        path={ROUTES.UNAUTHORIZED}
        element={
          <Lazy>
            <UnauthorizedPage />
          </Lazy>
        }
      />

      <Route element={<ProtectedRoute />}>
        {/* Outside AppLayout: the shell's own requests would be refused while a
            password change is pending. */}
        <Route element={<AuthLayout />}>
          <Route
            path={ROUTES.CHANGE_PASSWORD}
            element={
              <Lazy>
                <ChangePasswordPage />
              </Lazy>
            }
          />
          <Route
            path={ROUTES.PIN_SETUP}
            element={
              <Lazy>
                <PinSetupPage />
              </Lazy>
            }
          />
          <Route
            path={ROUTES.CHANGE_PIN}
            element={
              <Lazy>
                <ChangePinPage />
              </Lazy>
            }
          />
        </Route>
        <Route element={<AppLayout />}>
          <Route
            path={ROUTES.FORBIDDEN}
            element={
              <InShell>
                <ForbiddenPage />
              </InShell>
            }
          />

          <Route
            path={ROUTES.DASHBOARD}
            element={page('dashboard.view', <DashboardPage />)}
          />

          <Route
            path={ROUTES.MASTER_DATA}
            element={page('master_data.view', <MasterDataOverviewPage />)}
          />
          <Route
            path={ROUTES.CUSTOMERS}
            element={page('customers.view', <CustomersListPage />)}
          />
          <Route
            path={ROUTES.CUSTOMERS_CREATE}
            element={page('customers.create', <CustomerCreatePage />)}
          />
          <Route
            path={ROUTES.CUSTOMERS_EDIT}
            element={page('customers.update', <CustomerEditPage />)}
          />
          <Route
            path={ROUTES.CUSTOMERS_VIEW}
            element={page('customers.view', <CustomerViewPage />)}
          />
          <Route
            path={ROUTES.PRODUCTS}
            element={page('products.view', <DesignsListPage />)}
          />
          <Route
            path={ROUTES.PRODUCTS_CREATE}
            element={page('products.create', <DesignCreatePage />)}
          />
          <Route
            path={ROUTES.PRODUCT_VIEW}
            element={page('products.view', <ProductDetailPage />)}
          />
          <Route
            path={ROUTES.CATALOG}
            element={page('catalog.view', <CatalogListPage />)}
          />
          <Route
            path={ROUTES.CATALOG_CREATE}
            element={page('catalog.create', <CatalogFormPage />)}
          />
          <Route
            path={ROUTES.CATALOG_VIEW}
            element={page('catalog.view', <CatalogDetailPage />)}
          />
          <Route
            path={ROUTES.CATALOG_EDIT}
            element={page('catalog.update', <CatalogFormPage />)}
          />
          <Route
            path={ROUTES.CATALOG_SKUS}
            element={page('catalog.update', <CatalogSkusPage />)}
          />
          <Route
            path={ROUTES.CATEGORIES}
            element={page('categories.view', <CategoriesPage />)}
          />
          <Route
            path={ROUTES.CATEGORIES_VIEW}
            element={page('categories.view', <CategoryViewPage />)}
          />
          <Route
            path={ROUTES.QUALITIES}
            element={page('qualities.view', <QualitiesPage />)}
          />
          <Route
            path={ROUTES.COLOURS}
            element={page('colors.view', <ColoursPage />)}
          />
          <Route
            path={ROUTES.SUPPLIERS}
            element={page('suppliers.view', <SuppliersListPage />)}
          />
          <Route
            path={ROUTES.SUPPLIERS_CREATE}
            element={page('suppliers.create', <SupplierCreatePage />)}
          />
          <Route
            path={ROUTES.SUPPLIERS_EDIT}
            element={page('suppliers.update', <SupplierEditPage />)}
          />
          <Route
            path={ROUTES.SUPPLIERS_VIEW}
            element={page('suppliers.view', <SupplierViewPage />)}
          />
          <Route
            path={ROUTES.SALES_AGENTS}
            element={page('sales_agents.view', <SalesAgentsPage />)}
          />
          <Route
            path={ROUTES.WAREHOUSES}
            element={page('warehouses.view', <WarehousesPage />)}
          />

          <Route
            path={ROUTES.SALES_LEADS}
            element={page('sales_leads.view', <SalesLeadsListPage />)}
          />
          <Route
            path={ROUTES.SALES_LEADS_CREATE}
            element={page('sales_leads.create', <SalesLeadCreatePage />)}
          />
          <Route
            path={ROUTES.SALES_LEADS_REGISTER}
            element={page('sales_leads.register', <SalesLeadRegisterPage />)}
          />
          <Route
            path={ROUTES.SALES_LEADS_OLD_VIEW}
            element={page('old_customers.view', <OldCustomerViewPage />)}
          />
          <Route
            path={ROUTES.SALES_LEADS_INBOX_VIEW}
            element={page('sales_leads.inbox', <SiteInboxReviewPage />)}
          />
          <Route
            path={ROUTES.SALES_LEADS_VIEW}
            element={page('sales_leads.view', <SalesLeadViewPage />)}
          />

          <Route
            path={ROUTES.QUOTATIONS}
            element={page('quotations.view', <QuotationsListPage />)}
          />
          <Route
            path={ROUTES.QUOTATIONS_CREATE}
            element={page('quotations.create', <QuotationCreatePage />)}
          />
          <Route
            path={ROUTES.QUOTATION_VIEW}
            element={page('quotations.view', <QuotationDetailPage />)}
          />
          <Route
            path={ROUTES.ESTIMATES}
            element={page('estimates.view', <EstimatesListPage />)}
          />
          <Route
            path={ROUTES.ESTIMATES_CREATE}
            element={page('estimates.create', <EstimateCreatePage />)}
          />
          <Route
            path={ROUTES.ESTIMATE_VIEW}
            element={page('estimates.view', <EstimateDetailPage />)}
          />
          <Route
            path={ROUTES.COMPANY_BANK_ACCOUNTS}
            element={page('bank_accounts.view', <BankAccountsPage />)}
          />

          <Route
            path={ROUTES.REPORTS}
            element={page(
              ['purchase_reports.view', 'dispatch_reports.view', 'reports.view'],
              <ReportsLayout />,
            )}
          >
            <Route
              index
              element={
                <InShell>
                  <ReportsHomeRedirect />
                </InShell>
              }
            />
            <Route
              path="purchases/summary"
              element={page('purchase_reports.view', <SummaryOverviewPage />)}
            />
            <Route
              path="purchases/summary/trend"
              element={page('purchase_reports.view', <SummaryTrendPage />)}
            />
            {/* Old Category tab URL — the page is gone, but the bookmark
             * shouldn't 404. Send it to the Trend page, which now carries
             * a Top Categories Trend chart instead. */}
            <Route
              path="purchases/summary/breakdowns"
              element={<Navigate to={ROUTES.REPORTS_PURCHASE_TREND} replace />}
            />
            <Route
              path="dispatches/summary"
              element={page('dispatch_reports.view', <SummaryOverviewPage />)}
            />
            <Route
              path="dispatches/summary/trend"
              element={page('dispatch_reports.view', <SummaryTrendPage />)}
            />
            <Route
              path="dispatches/summary/breakdowns"
              element={<Navigate to={ROUTES.REPORTS_DISPATCH_TREND} replace />}
            />
            <Route
              path="exports"
              element={page('reports.view', <DetailExportsPage />)}
            />
            <Route
              path="summary"
              element={
                <InShell>
                  <ReportsHomeRedirect />
                </InShell>
              }
            />
            <Route
              path="summary/breakdowns"
              element={
                <InShell>
                  <ReportsHomeRedirect breakdowns />
                </InShell>
              }
            />
          </Route>

          <Route
            path={ROUTES.DISPATCH_SHEET}
            element={page('delivery_challans.view', <DispatchSheetListPage />)}
          />
          <Route
            path={ROUTES.DISPATCH_SHEET_CREATE}
            element={page('delivery_challans.create', <DispatchSheetCreatePage />)}
          />
          <Route
            path={ROUTES.DISPATCH_SHEET_EDIT}
            element={page('delivery_challans.update', <DispatchSheetEditPage />)}
          />
          <Route
            path={ROUTES.DISPATCH_SHEET_VIEW}
            element={page('delivery_challans.view', <DispatchSheetViewPage />)}
          />

          <Route
            path={ROUTES.PURCHASE_SHEET}
            element={page('goods_received.view', <PurchaseSheetListPage />)}
          />
          <Route
            path={ROUTES.PURCHASE_SHEET_CREATE}
            element={page('goods_received.create', <PurchaseSheetCreatePage />)}
          />
          <Route
            path={ROUTES.PURCHASE_SHEET_EDIT}
            element={page('goods_received.update', <PurchaseSheetEditPage />)}
          />
          <Route
            path={ROUTES.PURCHASE_SHEET_VIEW}
            element={page('goods_received.view', <PurchaseSheetViewPage />)}
          />

          <Route
            path={ROUTES.TASKS}
            element={page('tasks.view', <TasksPage />)}
          />
          <Route
            path={ROUTES.TASKS_CREATE}
            element={page('tasks.create', <TaskCreatePage />)}
          />
          <Route
            path={ROUTES.TASKS_EDIT}
            element={page('tasks.view', <TaskEditPage />)}
          />
          <Route
            path={ROUTES.TASKS_VIEW}
            element={page('tasks.view', <TaskDetailPage />)}
          />

          <Route
            path={ROUTES.MESSENGER}
            element={page('messages.view', <MessengerPage />)}
          />
          <Route
            path={ROUTES.MESSENGER_CHAT}
            element={page('messages.view', <MessengerPage />)}
          />

          <Route
            path={ROUTES.USERS}
            element={page('users.view', <UsersListPage />)}
          />
          <Route
            path={ROUTES.USERS_CREATE}
            element={page('users.create', <UserCreatePage />)}
          />
          <Route
            path={ROUTES.USERS_AGENT_LINKS}
            element={page('sales_agents.link', <AgentLinksPage />)}
          />
          <Route
            path={ROUTES.PERMISSIONS}
            element={page('permissions.view', <PermissionsPage />)}
          />
          <Route
            path={ROUTES.USERS_EDIT}
            element={page('users.update', <UserEditPage />)}
          />
          <Route
            path={ROUTES.USERS_VIEW}
            element={page('users.view', <UserViewPage />)}
          />

          <Route
            path={ROUTES.WORKSPACE}
            element={
              <InShell>
                <WorkspacePage />
              </InShell>
            }
          />
          <Route
            path={ROUTES.MORE}
            element={<Navigate to={ROUTES.WORKSPACE} replace />}
          />
          <Route
            path={ROUTES.NOTIFICATIONS}
            element={
              <InShell>
                <NotificationsPage />
              </InShell>
            }
          />
          <Route
            path={ROUTES.MOBILE_SHARE}
            element={
              <InShell>
                <MobileSharePage />
              </InShell>
            }
          />
          <Route
            path={ROUTES.ACCOUNT}
            element={
              <InShell>
                <MobileSettingsPage />
              </InShell>
            }
          />
          <Route
            path={ROUTES.MY_DEVICES}
            element={
              <InShell>
                <MyDevicesPage />
              </InShell>
            }
          />
          <Route
            path={ROUTES.SETTINGS}
            element={page(['sheets_sync.run', 'auth_settings.manage', 'backups.view', 'mail.view', 'site_form.manage'], <SettingsPage />)}
          />
          <Route
            path={ROUTES.SETTINGS_SECURITY}
            element={page('auth_settings.manage', <SecuritySettingsPage />)}
          />
          <Route
            path={ROUTES.SETTINGS_BACKUPS}
            element={page('backups.view', <BackupsPage />)}
          />
          <Route
            path={ROUTES.SETTINGS_EMAIL}
            element={page('mail.view', <EmailSettingsPage />)}
          />
          <Route
            path={ROUTES.SETTINGS_SITE_FORM}
            element={page('site_form.manage', <SiteFormSettingsPage />)}
          />
          <Route
            path={ROUTES.SETTINGS_SHEETS_SYNC}
            element={page('sheets_sync.run', <SheetsSyncPage />)}
          />
          <Route
            path={ROUTES.QUOTATION_TERMS_TEMPLATES}
            element={page('quotation_terms.view', <QuotationTermsTemplatesPage />)}
          />
          <Route
            path={ROUTES.QUOTATION_TERMS_TEMPLATES_LEGACY}
            element={<Navigate to={ROUTES.QUOTATION_TERMS_TEMPLATES} replace />}
          />
        </Route>
      </Route>

      <Route
        path="*"
        element={
          <Lazy>
            <NotFoundPage />
          </Lazy>
        }
      />
    </Route>
  )),
)

installListMemory(appRouter, REMEMBERED_LIST_PATHS)

setPendingHrefGetter(() => {
  const next = appRouter.state.navigation.location
  return next ? `${next.pathname}${next.search}` : null
})
